/**
 * DailyChallengeWidget — three daily challenges with progress bars.
 * Server tracks progress + auto-completes when target hit (PROG-4b).
 */
import { useEffect, useState } from 'react';
import { Target, CheckCircle2 } from 'lucide-react';
import { getCharacterStats, type CharacterStats } from '../../lib/api';

interface Props {
  charId: string;
  refreshKey?: number | string;
  compact?: boolean;
  /** When true, render the challenges list inline without the outer
   *  `<section>` chrome + header. Used by Home.tsx where the surrounding
   *  card already provides the header and warm-wood frame. */
  embedded?: boolean;
}

export function DailyChallengeWidget({ charId, refreshKey, compact, embedded }: Props) {
  const [stats, setStats] = useState<CharacterStats | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const s = await getCharacterStats(charId);
        if (!live) return;
        setStats(s);
      } catch { /* */ }
    })();
    return () => { live = false; };
  }, [charId, refreshKey]);

  const challenges = stats?.daily?.challenges ?? [];
  const completed = challenges.filter((c) => c.completed).length;
  if (challenges.length === 0) return null;

  const list = (
    <ul>
      {challenges.map((c) => {
        const pct = Math.min(100, Math.max(0, (c.progress / Math.max(1, c.target)) * 100));
        return (
          <li key={c.id} className={`daily-row${c.completed ? ' done' : ''}`}>
            <div className="daily-row-head">
              {c.completed ? <CheckCircle2 size={14} /> : <span aria-hidden="true">○</span>}
              <span className="daily-label">{c.label}</span>
              <span className="daily-progress-num">{c.progress}/{c.target}</span>
            </div>
            <div className="daily-track" aria-hidden="true">
              <span style={{ width: `${pct}%` }} />
            </div>
            <small className="daily-reward">+{c.rewardSeasonXp} SXP · +{c.rewardEmbers} 🔥</small>
          </li>
        );
      })}
    </ul>
  );

  if (embedded) {
    return <div className="daily-widget embedded" aria-label="Daily challenges">{list}</div>;
  }

  return (
    <section className={`daily-widget${compact ? ' compact' : ''}`} aria-label="Daily challenges">
      <header>
        <Target size={16} aria-hidden="true" />
        <strong>Daily challenges</strong>
        <small className="muted">{completed} / {challenges.length} done</small>
      </header>
      {list}
    </section>
  );
}
